import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Clock, Layers } from "lucide-react";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};
const item = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.21, 0.61, 0.35, 1] } },
};

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const TRUST_CHIPS = [
  { icon: Clock, label: "24/7 support ready" },
  { icon: Layers, label: "10 services, one partner" },
];

export const Hero = () => {
  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      <div className="hero-overlay" />
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative mx-auto flex max-w-[var(--container-max)] flex-col items-center px-4 pb-20 pt-12 text-center sm:px-6 sm:pt-16"
      >
        {/* Logo - top middle */}
        <motion.img
          variants={item}
          src="/logo.png"
          alt="GrowthSync Solutions logo"
          width="176"
          height="176"
          loading="eager"
          decoding="async"
          data-testid="hero-logo"
          className="h-36 w-36 rounded-2xl shadow-[var(--shadow-gold)] sm:h-44 sm:w-44"
        />

        {/* Big brand wordmark matching logo style */}
        <motion.h1
          variants={item}
          data-testid="hero-brand-name"
          className="font-display mt-8 text-5xl font-bold leading-none sm:text-6xl lg:text-7xl"
        >
          <span className="text-white">Growth</span>
          <span className="text-[color:var(--gs-gold-400)]">Sync</span>
        </motion.h1>

        <motion.div variants={item} className="mt-3 flex items-center gap-4">
          <span className="gold-hairline w-10 sm:w-16" />
          <span className="font-display text-sm uppercase tracking-[0.4em] text-white/90 sm:text-lg">
            Solutions
          </span>
          <span className="gold-hairline w-10 sm:w-16" />
        </motion.div>

        <motion.p
          variants={item}
          className="mt-4 text-[11px] font-semibold uppercase tracking-[0.3em] text-[color:var(--gs-gold-400)] sm:text-xs"
          data-testid="hero-tagline"
        >
          Strategize • Connect • Grow
        </motion.p>

        {/* Value proposition */}
        <motion.p
          variants={item}
          className="mt-8 max-w-2xl text-base leading-relaxed text-white/75 sm:text-lg"
          data-testid="hero-value-prop"
        >
          Telemarketing, lead generation, AI-driven marketing, and full-stack BPO —
          delivered with enterprise-grade execution from Dhaka, Bangladesh. One partner
          for every system that grows your revenue.
        </motion.p>

        {/* CTAs */}
        <motion.div variants={item} className="mt-9 flex flex-wrap items-center justify-center gap-4">
          <Button
            size="lg"
            onClick={() => scrollTo("#contact")}
            data-testid="hero-request-quote-button"
            className="h-12 bg-[color:var(--gs-gold-400)] px-8 text-base font-semibold text-[color:var(--gs-navy-950)] shadow-[var(--shadow-elev-1)] hover:bg-[color:var(--gs-gold-500)] focus-visible:ring-2 focus-visible:ring-[color:var(--gs-gold-400)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--gs-navy-900)] active:scale-[0.98]"
          >
            Request a Quote
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={() => scrollTo("#services")}
            data-testid="hero-view-services-button"
            className="h-12 border-white/15 bg-white/5 px-8 text-base text-white hover:border-[color:var(--gs-line)] hover:bg-white/10 hover:text-white active:scale-[0.98]"
          >
            View Services
          </Button>
        </motion.div>

        {/* Trust chips */}
        <motion.div variants={item} className="mt-12 flex flex-wrap items-center justify-center gap-3">
          {TRUST_CHIPS.map((chip) => (
            <div
              key={chip.label}
              className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-white/70 sm:text-sm"
            >
              <chip.icon className="h-3.5 w-3.5 text-[color:var(--gs-gold-400)]" />
              {chip.label}
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
